import { useState } from "react";
import MainLayout from "@/components/feature/MainLayout";
import { salesRecords } from "@/mocks/sales";
import { allUsers } from "@/mocks/users";
import PurchaseHistoryChart from "./components/PurchaseHistoryChart";
import AIReportPanel from "./components/AIReportPanel";

const statusColors: Record<string, string> = {
  成功: "bg-green-50 text-green-600",
  失败: "bg-red-50 text-red-600",
  处理中: "bg-amber-50 text-amber-600",
};

const iotColors: Record<string, string> = {
  已下发: "text-green-600",
  下发失败: "text-red-500",
  待下发: "text-amber-500",
};

export default function SalesRecordsPage() {
  const [selectedUserId, setSelectedUserId] = useState<string>("C001");
  const [searchUser, setSearchUser] = useState("");
  const [activeTab, setActiveTab] = useState<"records" | "chart" | "ai">("chart");

  const selectedUser = allUsers.find(u => u.id === selectedUserId);
  const userRecords = salesRecords.filter(r => r.userId === selectedUserId);

  const matchedUsers = searchUser.length > 0
    ? allUsers.filter(u => u.name.includes(searchUser) || u.phone.includes(searchUser))
    : [];

  const totalAmount = userRecords.reduce((s, r) => s + r.amount, 0);
  const totalVolume = userRecords.reduce((s, r) => s + r.gasVolume, 0);

  return (
    <MainLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">售气记录分析</h2>
            <p className="text-xs text-gray-400 mt-0.5">购气历史曲线对比 · AI智能分析报告</p>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#E8531A] text-white text-sm rounded-lg hover:bg-[#d44a15] transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line"></i>新增购气
          </button>
        </div>

        <div className="grid grid-cols-12 gap-4">
          {/* Left: User Selector */}
          <div className="col-span-3 space-y-3">
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <h3 className="text-xs font-semibold text-gray-700 mb-3">选择用户</h3>
              <div className="relative mb-3">
                <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                <input
                  value={searchUser}
                  onChange={e => setSearchUser(e.target.value)}
                  placeholder="搜索用户..."
                  className="w-full pl-7 pr-2 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50"
                />
              </div>
              {matchedUsers.length > 0 && (
                <div className="mb-2 border border-gray-100 rounded-lg overflow-hidden">
                  {matchedUsers.map(u => (
                    <div
                      key={u.id}
                      onClick={() => { setSelectedUserId(u.id); setSearchUser(""); }}
                      className="px-3 py-2 text-xs hover:bg-orange-50 cursor-pointer border-b border-gray-50 last:border-0"
                    >
                      <p className="font-medium text-gray-800">{u.name}</p>
                      <p className="text-gray-400">{u.phone}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="space-y-1.5 max-h-80 overflow-y-auto">
                {allUsers.map(u => (
                  <div
                    key={u.id}
                    onClick={() => setSelectedUserId(u.id)}
                    className={`flex items-center gap-2 p-2.5 rounded-lg cursor-pointer transition-colors ${
                      selectedUserId === u.id ? "bg-orange-50 border border-[#E8531A]/20" : "hover:bg-gray-50"
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                      selectedUserId === u.id ? "bg-[#E8531A]" : "bg-gray-100"
                    }`}>
                      <span className={`text-xs font-bold ${selectedUserId === u.id ? "text-white" : "text-gray-500"}`}>
                        {u.name[0]}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800 truncate">{u.name}</p>
                      <p className="text-xs text-gray-400 truncate">{u.category}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* User Quick Info */}
            {selectedUser && (
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded-full bg-[#E8531A] flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{selectedUser.name[0]}</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{selectedUser.name}</p>
                    <p className="text-xs text-gray-400">{selectedUser.category}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">当前余额</span>
                    <span className={`font-semibold ${selectedUser.balance < 200 ? "text-red-600" : "text-gray-800"}`}>
                      ¥{selectedUser.balance.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">本月用气</span>
                    <span className="font-semibold text-gray-800">{selectedUser.monthlyUsage.toLocaleString()} m³</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">安全评级</span>
                    <span className="font-semibold text-green-600">{selectedUser.safetyGrade}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">本期购气</span>
                    <span className="font-semibold text-[#E8531A]">¥{totalAmount.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right: Main Content */}
          <div className="col-span-9 space-y-4">
            {/* Tabs */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
              {[
                { key: "chart", label: "历史曲线对比", icon: "ri-line-chart-line" },
                { key: "records", label: "购气记录", icon: "ri-receipt-line" },
                { key: "ai", label: "AI分析报告", icon: "ri-robot-2-line" },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as typeof activeTab)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === tab.key ? "bg-white text-gray-900 font-semibold" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <i className={tab.icon}></i>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Chart Tab */}
            {activeTab === "chart" && <PurchaseHistoryChart />}

            {/* Records Tab */}
            {activeTab === "records" && (
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-gray-900">购气记录明细</h3>
                  <div className="flex items-center gap-3">
                    <div className="text-xs text-gray-500">
                      共 <span className="font-semibold text-gray-800">{userRecords.length}</span> 条记录 ·
                      合计 <span className="font-semibold text-[#E8531A]">¥{totalAmount.toLocaleString()}</span> ·
                      <span className="font-semibold text-gray-700"> {totalVolume.toFixed(2)} m³</span>
                    </div>
                    <button className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                      <i className="ri-download-line"></i>导出
                    </button>
                  </div>
                </div>
                {userRecords.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <div className="w-12 h-12 flex items-center justify-center mx-auto mb-2">
                      <i className="ri-inbox-line text-4xl text-gray-200"></i>
                    </div>
                    <p className="text-sm">该用户暂无购气记录</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-100">
                          {["记录编号", "购气金额", "气量(m³)", "支付方式", "支付时间", "状态", "IoT下发", "操作"].map(h => (
                            <th key={h} className="text-left py-2.5 px-3 text-gray-400 font-medium whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {userRecords.map(r => (
                          <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                            <td className="py-3 px-3 font-mono text-gray-600">{r.id}</td>
                            <td className="py-3 px-3 font-bold text-[#E8531A]">¥{r.amount.toLocaleString()}</td>
                            <td className="py-3 px-3 text-gray-700">{r.gasVolume.toFixed(2)}</td>
                            <td className="py-3 px-3 text-gray-600">{r.payMethod}</td>
                            <td className="py-3 px-3 text-gray-500 whitespace-nowrap">{r.payTime}</td>
                            <td className="py-3 px-3">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${statusColors[r.status] || "bg-gray-50 text-gray-500"}`}>
                                {r.status}
                              </span>
                            </td>
                            <td className="py-3 px-3">
                              <span className={`text-xs font-medium ${iotColors[r.iotStatus] || "text-gray-500"}`}>
                                {r.iotStatus}
                              </span>
                            </td>
                            <td className="py-3 px-3">
                              <div className="flex items-center gap-1.5">
                                <button className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 cursor-pointer whitespace-nowrap">
                                  小票
                                </button>
                                <button className="px-2 py-1 text-xs bg-orange-50 text-[#E8531A] rounded hover:bg-orange-100 cursor-pointer whitespace-nowrap">
                                  发票
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* All records summary */}
                <div className="mt-4 pt-4 border-t border-gray-50">
                  <h4 className="text-xs font-semibold text-gray-700 mb-3">全部购气记录（所有用户）</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-100">
                          {["用户", "金额", "气量", "支付方式", "时间", "状态"].map(h => (
                            <th key={h} className="text-left py-2 px-3 text-gray-400 font-medium whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {salesRecords.map(r => (
                          <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="py-2.5 px-3">
                              <div>
                                <p className="font-medium text-gray-800">{r.userName}</p>
                                <p className="text-gray-400">{r.phone}</p>
                              </div>
                            </td>
                            <td className="py-2.5 px-3 font-bold text-[#E8531A]">¥{r.amount.toLocaleString()}</td>
                            <td className="py-2.5 px-3 text-gray-600">{r.gasVolume.toFixed(2)} m³</td>
                            <td className="py-2.5 px-3 text-gray-500">{r.payMethod}</td>
                            <td className="py-2.5 px-3 text-gray-400 whitespace-nowrap">{r.payTime}</td>
                            <td className="py-2.5 px-3">
                              <span className={`px-2 py-0.5 rounded-full ${statusColors[r.status] || ""}`}>{r.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* AI Report Tab */}
            {activeTab === "ai" && <AIReportPanel />}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
